/**
 * AssetImage — an image named by a reference found in widget data.
 *
 * Widget payloads carry image *references* (a tenant logo, a country flag, a
 * screenshot), never bundled files; the host resolves them through
 * `WidgetAssetsContext`. An unresolved reference renders nothing (or the
 * `fallback`), so data without images keeps working.
 */
import { useContext, type ReactNode } from "react";

import { WidgetAssetsContext } from "../contexts/WidgetFeaturesContext";

/** The URL for an image reference, or `undefined` when the host does not know it. */
export function useAssetUrl(reference: string | null | undefined): string | undefined {
  const resolve = useContext(WidgetAssetsContext);
  return reference ? resolve(reference) : undefined;
}

export function AssetImage({
  reference,
  alt = "",
  className = "",
  fallback = null,
}: {
  reference: string | null | undefined;
  /** Empty (decorative) by default: the text next to the image names it. */
  alt?: string;
  className?: string;
  fallback?: ReactNode;
}) {
  const url = useAssetUrl(reference);
  if (!url) return <>{fallback}</>;
  return <img src={url} alt={alt} loading="lazy" className={className} />;
}
